import React from '../vendor/react';
import { AlertTriangle, CircleDollarSign, History, Plus } from '../vendor/lucide-react';
import { useEffect, useMemo, useState } from '../vendor/react';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { cashBreakdownTotal, MXN_CASH_DENOMINATIONS, type CashDenomination } from '../features/sales/cash-count';
import { paymentLabels } from '../features/sales/sales-utils';
import { useSales } from '../features/sales/useSales';
import { formatMoney } from '../lib/money';
import type { CashDenominationCount, CashMovementType, CashSession, PaymentMethod } from '../types/domain';

const movementLabels: Record<CashMovementType, string> = {
  sale: 'Venta',
  deposit: 'Anticipo',
  payment: 'Abono',
  income: 'Ingreso extraordinario',
  withdrawal: 'Retiro',
  expense: 'Gasto',
  purchase: 'Compra a proveedor',
  refund: 'Reembolso',
};

type CloseCashInput = {
  cashBreakdown: CashDenominationCount[];
  cardCountedCents: number;
  notes: string;
};

export function CashPage() {
  const sales = useSales();
  const cashControlEnabled = sales.business?.cashControlEnabled !== false;
  const [openModal, setOpenModal] = useState(false);
  const [movementModal, setMovementModal] = useState(false);
  const [closeModal, setCloseModal] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const session = sales.openSession;
  const movements = useMemo(
    () => session ? sales.cashMovements.filter((movement) => movement.cashSessionId === session.id) : [],
    [sales.cashMovements, session?.id],
  );
  const cashNet = movements
    .filter((movement) => movement.method === 'cash')
    .reduce((sum, movement) => sum + (movement.direction === 'in' ? movement.amountCents : -movement.amountCents), 0);
  const expectedCash = (session?.openingFloatCents ?? 0) + cashNet;
  const netByMethod = (method: PaymentMethod) => movements
    .filter((movement) => movement.method === method)
    .reduce((sum, movement) => sum + (movement.direction === 'in' ? movement.amountCents : -movement.amountCents), 0);
  const expectedCard = netByMethod('card');
  const closedSessions = useMemo(
    () => sales.cashSessions
      .filter((item) => item.branchId === sales.branch?.id && ['closed', 'difference'].includes(item.status))
      .sort((left, right) => (right.closedAt ?? right.openedAt).localeCompare(left.closedAt ?? left.openedAt))
      .slice(0, 8),
    [sales.cashSessions, sales.branch?.id],
  );

  const run = (callback: () => void, message: string) => {
    try {
      callback();
      setNotice(message);
      setError('');
      setOpenModal(false);
      setMovementModal(false);
      setCloseModal(false);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se pudo completar la operación.');
    }
  };

  if (!cashControlEnabled) {
    return (
      <div className="module-page cash-page">
        <header className="module-heading">
          <div>
            <span>{sales.business?.name} · {sales.branch?.name}</span>
            <h1>Caja y corte</h1>
            <p>Este negocio está configurado para operar sin control de caja obligatorio.</p>
          </div>
        </header>
        <section className="cash-empty">
          <span><CircleDollarSign size={30} /></span>
          <h2>Control de caja desactivado</h2>
          <p>Puedes registrar apartados, encargos, ventas móviles, transferencias, gastos y compras sin abrir una sesión. Activa el control de caja cuando necesites fondos iniciales, conteo de efectivo y cortes por turno.</p>
          <Badge tone="info">Operación móvil o sin local</Badge>
        </section>
      </div>
    );
  }

  return (
    <div className="module-page cash-page">
      <header className="module-heading">
        <div>
          <span>{sales.business?.name} · {sales.branch?.name}</span>
          <h1>Caja y corte</h1>
          <p>Cuenta billetes y monedas, compara la terminal externa y conserva un corte auditable.</p>
        </div>
        <div className="module-heading__actions">
          {session ? (
            <>
              <Button variant="secondary" icon={<Plus size={17} />} onClick={() => setMovementModal(true)}>Movimiento</Button>
              <Button onClick={() => setCloseModal(true)}>Contar y cerrar</Button>
            </>
          ) : (
            <Button icon={<CircleDollarSign size={18} />} onClick={() => setOpenModal(true)}>Abrir caja</Button>
          )}
        </div>
      </header>

      {notice && <button className="module-notice" onClick={() => setNotice('')}>{notice}<span>×</span></button>}
      {error && <button className="module-notice is-error" onClick={() => setError('')}>{error}<span>×</span></button>}

      {!session ? (
        <section className="cash-empty">
          <span><CircleDollarSign size={30} /></span>
          <h2>La caja está cerrada</h2>
          <p>Abre una sesión para permitir ventas, anticipos, apartados, encargos y movimientos de efectivo.</p>
          <Button onClick={() => setOpenModal(true)}>Abrir {sales.branch?.registerName ?? 'caja principal'}</Button>
        </section>
      ) : (
        <>
          <section className="cash-session-banner">
            <div>
              <Badge tone="success">Sesión abierta</Badge>
              <h2>{session.registerName}</h2>
              <p>Desde {new Date(session.openedAt).toLocaleString('es-MX')} · fondo inicial {formatMoney(session.openingFloatCents)}</p>
            </div>
            <div>
              <span>Efectivo esperado</span>
              <strong>{formatMoney(expectedCash)}</strong>
            </div>
          </section>
          <section className="inventory-metrics">
            <article><span>Efectivo cobrado</span><strong>{formatMoney(netByMethod('cash'))}</strong><small>Sin contar fondo inicial</small></article>
            <article><span>Transferencias</span><strong>{formatMoney(netByMethod('transfer'))}</strong><small>Movimientos registrados</small></article>
            <article><span>Tarjeta registrada</span><strong>{formatMoney(expectedCard)}</strong><small>Se comparará con la terminal</small></article>
            <article><span>Movimientos</span><strong>{movements.length}</strong><small>Entradas y salidas auditables</small></article>
          </section>
          <section className="data-panel">
            <div className="data-toolbar"><span className="toolbar-context"><History size={16} /> Movimientos de la sesión</span></div>
            <div className="cash-movement-list">
              {movements.map((movement) => (
                <article key={movement.id}>
                  <span className={movement.direction === 'in' ? 'positive' : 'negative'}>{movement.direction === 'in' ? '+' : '−'}{formatMoney(movement.amountCents)}</span>
                  <div><strong>{movementLabels[movement.type]}</strong><small>{movement.reference} · {paymentLabels[movement.method]}</small></div>
                  <time>{new Date(movement.createdAt).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</time>
                </article>
              ))}
              {movements.length === 0 && <div className="data-empty"><History size={30} /><strong>Sin movimientos</strong><p>Las operaciones del POS aparecerán automáticamente.</p></div>}
            </div>
          </section>
        </>
      )}

      <RecentCashSessions sessions={closedSessions} />

      <OpenCashModal
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={(amount, notes) => run(() => sales.actions.openCash({ openingFloatCents: amount, notes }), 'Caja abierta correctamente.')}
      />
      <MovementModal
        open={movementModal}
        onClose={() => setMovementModal(false)}
        onSave={(input) => run(() => sales.actions.cashMovement(input), 'Movimiento de caja registrado.')}
      />
      <CloseCashModal
        open={closeModal}
        expectedCash={expectedCash}
        expectedCard={expectedCard}
        onClose={() => setCloseModal(false)}
        onSave={(input) => session && run(
          () => sales.actions.closeCash({ sessionId: session.id, ...input }),
          cashBreakdownTotal(input.cashBreakdown) === expectedCash && input.cardCountedCents === expectedCard
            ? 'Caja cerrada sin diferencias.'
            : 'Caja cerrada con diferencias documentadas.',
        )}
      />
    </div>
  );
}

function RecentCashSessions({ sessions }: { sessions: CashSession[] }) {
  if (!sessions.length) return null;
  return (
    <section className="panel cash-history-panel">
      <div className="panel-heading">
        <div><span>Historial</span><h2>Últimos cortes de caja</h2></div>
        <Badge>{sessions.length} cortes</Badge>
      </div>
      <div className="cash-close-list">
        {sessions.map((session) => {
          const billCount = session.closingCashBreakdown?.filter((item) => item.kind === 'bill').reduce((sum, item) => sum + item.quantity, 0) ?? 0;
          const coinCount = session.closingCashBreakdown?.filter((item) => item.kind === 'coin').reduce((sum, item) => sum + item.quantity, 0) ?? 0;
          return (
            <article key={session.id}>
              <header>
                <div><strong>{session.registerName}</strong><small>{new Date(session.closedAt ?? session.openedAt).toLocaleString('es-MX')}</small></div>
                <Badge tone={session.status === 'difference' ? 'warning' : 'success'}>{session.status === 'difference' ? 'Con diferencia' : 'Cuadrada'}</Badge>
              </header>
              <div className="cash-close-metrics">
                <div><span>Efectivo contado</span><strong>{formatMoney(session.countedCents ?? 0)}</strong><small>Diferencia {formatMoney(session.differenceCents ?? 0)}</small></div>
                <div><span>Terminal externa</span><strong>{formatMoney(session.cardCountedCents ?? 0)}</strong><small>Diferencia {formatMoney(session.cardDifferenceCents ?? 0)}</small></div>
                <div><span>Conteo</span><strong>{billCount} billetes</strong><small>{coinCount} monedas</small></div>
              </div>
              {Boolean(session.closingCashBreakdown?.length) && (
                <details>
                  <summary>Ver desglose de efectivo</summary>
                  <div className="cash-breakdown-chips">
                    {session.closingCashBreakdown?.map((item) => (
                      <span key={`${item.kind}-${item.denominationCents}`}>{item.kind === 'bill' ? 'Billete' : 'Moneda'} {formatMoney(item.denominationCents)} × {item.quantity}</span>
                    ))}
                  </div>
                </details>
              )}
              {session.notes && <p>{session.notes}</p>}
            </article>
          );
        })}
      </div>
    </section>
  );
}

function OpenCashModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (amount: number, notes: string) => void }) {
  const [amount, setAmount] = useState('1000');
  const [notes, setNotes] = useState('');
  return (
    <Modal open={open} onClose={onClose} title="Abrir caja">
      <div className="movement-form">
        <label className="field"><span>Fondo inicial</span><input type="number" min="0" step="0.01" value={amount} onChange={(event) => setAmount(event.target.value)} autoFocus /></label>
        <label className="field"><span>Observaciones</span><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        <div className="modal-actions"><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button onClick={() => onSave(Math.round(Number(amount) * 100), notes)}>Abrir caja</Button></div>
      </div>
    </Modal>
  );
}

function MovementModal({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (input: { type: CashMovementType; method: PaymentMethod; amountCents: number; direction: 'in' | 'out'; reference: string; notes: string }) => void }) {
  const [type, setType] = useState<CashMovementType>('expense');
  const [method, setMethod] = useState<PaymentMethod>('cash');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [notes, setNotes] = useState('');
  const direction: 'in' | 'out' = type === 'income' ? 'in' : 'out';
  return (
    <Modal open={open} onClose={onClose} title="Registrar movimiento">
      <div className="movement-form">
        <label className="field"><span>Tipo</span><select value={type} onChange={(event) => setType(event.target.value as CashMovementType)}><option value="expense">Gasto</option><option value="withdrawal">Retiro</option><option value="income">Ingreso extraordinario</option></select></label>
        <div className="form-grid form-grid--2">
          <label className="field"><span>Método</span><select value={method} onChange={(event) => setMethod(event.target.value as PaymentMethod)}><option value="cash">Efectivo</option><option value="transfer">Transferencia</option><option value="card">Tarjeta</option></select></label>
          <label className="field"><span>Importe</span><input value={amount} onChange={(event) => setAmount(event.target.value)} type="number" min="0.01" step="0.01" /></label>
        </div>
        <label className="field"><span>Referencia</span><input value={reference} onChange={(event) => setReference(event.target.value)} required placeholder="Motivo o comprobante" /></label>
        <label className="field"><span>Notas</span><textarea rows={2} value={notes} onChange={(event) => setNotes(event.target.value)} /></label>
        <div className="modal-actions"><Button variant="ghost" onClick={onClose}>Cancelar</Button><Button disabled={!reference.trim() || Number(amount) <= 0} onClick={() => onSave({ type, method, amountCents: Math.round(Number(amount) * 100), direction, reference, notes })}>Registrar {direction === 'in' ? 'entrada' : 'salida'}</Button></div>
      </div>
    </Modal>
  );
}

const denominationKey = (denomination: Pick<CashDenomination, 'kind' | 'denominationCents'>) => `${denomination.kind}-${denomination.denominationCents}`;
const emptyQuantities = () => Object.fromEntries(MXN_CASH_DENOMINATIONS.map((denomination) => [denominationKey(denomination), ''])) as Record<string, string>;

function CloseCashModal({ open, expectedCash, expectedCard, onClose, onSave }: { open: boolean; expectedCash: number; expectedCard: number; onClose: () => void; onSave: (input: CloseCashInput) => void }) {
  const [quantities, setQuantities] = useState<Record<string, string>>(emptyQuantities);
  const [cardCounted, setCardCounted] = useState('');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (!open) return;
    setQuantities(emptyQuantities());
    setCardCounted(expectedCard === 0 ? '0' : '');
    setNotes('');
  }, [open, expectedCard]);

  const cashBreakdown = MXN_CASH_DENOMINATIONS.map((denomination) => ({
    denominationCents: denomination.denominationCents,
    kind: denomination.kind,
    quantity: Math.max(0, Math.floor(Number(quantities[denominationKey(denomination)] || 0))),
  })).filter((item) => item.quantity > 0);
  const countedCash = cashBreakdownTotal(cashBreakdown);
  const cashDifference = countedCash - expectedCash;
  const cardProvided = cardCounted.trim() !== '' && Number.isFinite(Number(cardCounted));
  const cardCountedCents = cardProvided ? Math.round(Number(cardCounted) * 100) : 0;
  const cardDifference = cardCountedCents - expectedCard;
  const hasDifference = cashDifference !== 0 || cardDifference !== 0;
  const invalidCard = !cardProvided || cardCountedCents < 0;

  const renderDenominations = (kind: CashDenomination['kind']) => (
    <section className="cash-count-section">
      <header><strong>{kind === 'bill' ? 'Billetes' : 'Monedas'}</strong><span>Escribe cuántos hay de cada uno</span></header>
      <div className="denomination-grid">
        {MXN_CASH_DENOMINATIONS.filter((item) => item.kind === kind).map((denomination) => {
          const key = denominationKey(denomination);
          const quantity = Math.max(0, Math.floor(Number(quantities[key] || 0)));
          return (
            <label className="denomination-field" key={key}>
              <span>{denomination.label}</span>
              <input
                aria-label={`${kind === 'bill' ? 'Billetes' : 'Monedas'} de ${denomination.label}`}
                type="number"
                min="0"
                step="1"
                inputMode="numeric"
                value={quantities[key] ?? ''}
                onChange={(event) => setQuantities((current) => ({ ...current, [key]: event.target.value }))}
              />
              <small>{formatMoney(denomination.denominationCents * quantity)}</small>
            </label>
          );
        })}
      </div>
    </section>
  );

  return (
    <Modal open={open} onClose={onClose} title="Conteo y cierre de caja" size="wide">
      <div className="movement-form cash-count-form">
        <div className="cash-count-intro">
          <strong>Cuenta el efectivo físicamente</strong>
          <p>El total se calcula con los billetes y monedas. No necesitas escribir el efectivo contado por separado.</p>
        </div>
        {renderDenominations('bill')}
        {renderDenominations('coin')}

        <section className="terminal-count-section">
          <div><strong>Terminal de tarjeta externa</strong><p>Escribe manualmente el total que muestra tu terminal. NEXO solo lo compara; no se conecta ni realiza el cobro.</p></div>
          <label className="field"><span>Total mostrado por la terminal</span><input value={cardCounted} onChange={(event) => setCardCounted(event.target.value)} type="number" min="0" step="0.01" inputMode="decimal" placeholder="0.00" /></label>
        </section>

        <div className="cash-reconciliation-grid">
          <article><span>Efectivo esperado</span><strong>{formatMoney(expectedCash)}</strong></article>
          <article><span>Efectivo contado</span><strong>{formatMoney(countedCash)}</strong></article>
          <article className={cashDifference === 0 ? 'is-ok' : 'is-different'}><span>Diferencia efectivo</span><strong>{formatMoney(cashDifference)}</strong></article>
          <article><span>Tarjeta registrada</span><strong>{formatMoney(expectedCard)}</strong></article>
          <article><span>Total de terminal</span><strong>{cardProvided ? formatMoney(cardCountedCents) : 'Pendiente'}</strong></article>
          <article className={!invalidCard && cardDifference === 0 ? 'is-ok' : 'is-different'}><span>Diferencia tarjeta</span><strong>{cardProvided ? formatMoney(cardDifference) : 'Pendiente'}</strong></article>
        </div>

        {hasDifference && !invalidCard && <div className="inline-error"><AlertTriangle size={16} /> Explica cualquier sobrante, faltante o diferencia con la terminal antes de cerrar.</div>}
        {invalidCard && <div className="inline-error"><AlertTriangle size={16} /> Captura el total que muestra la terminal, aunque sea $0.00.</div>}
        <label className="field"><span>Observaciones o motivo de diferencia</span><textarea rows={3} value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Ejemplo: propina procesada directamente en la terminal" /></label>
        <div className="modal-actions">
          <Button variant="ghost" onClick={onClose}>Cancelar</Button>
          <Button disabled={invalidCard || (hasDifference && !notes.trim())} onClick={() => onSave({ cashBreakdown, cardCountedCents, notes })}>Guardar conteo y cerrar</Button>
        </div>
      </div>
    </Modal>
  );
}
